import { submitFormJson } from 'widgets/toolbox/ajax';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { TWidget } from 'widgets/Widget';

type TBonusChoiceResponse = TServerResponse & {
    cart?: object;
    errorMessage?: string;
    items?: Array<unknown>;
};

/** Selection tile wrapper — matches data-ref="option" in orderLevelBonusChoice.isml */
const OPTION_SELECTOR = '[data-ref="option"]';
/** Confirmed gift remove controls — data attrs from the summary remove button */
const CONFIRMED_REMOVE_SELECTOR = 'button[data-bonus-product-line-item="true"]';
/** Choice inputs in the selection list */
const BONUS_OPTION_INPUT_SELECTOR = 'input[data-bonus-option]';
/** Cart re-render after bonus add/remove */
const EVENT_BONUS_UPDATED = 'bonus.updated';
/** Keep header minicart qty in sync after bonus changes */
const EVENT_MINICART_NEED_UPDATE = 'minicart.need.update';
/**
 * BDLI UUIDs that must start collapsed after a gift delete re-renders the cart.
 * Survives widget destroy/recreate from CartMgr.renderCart.
 */
const collapseAfterRemoveUuids = new Set<string>();

/**
 * @description Order-level bonus choice on the cart page.
 * Renders radios when one gift can be selected and checkboxes when more are allowed.
 * @param Widget Base widget for extending
 * @returns OrderBonusChoice widget
 */
function OrderBonusChoiceClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory cart
     * @class OrderBonusChoice
     * @augments Widget
     * @property {string} data-widget - Widget name `orderBonusChoice`
     * @property {string} data-uuid - Bonus discount line item UUID
     * @property {number} data-max-bonus-items - Maximum gifts the customer can confirm
     */
    class OrderBonusChoice extends Widget {
        prefs() {
            return {
                uuid: '',
                maxBonusItems: 1,
                actionUrl: '',
                maxExceededMessage: '',
                errorMessage: '',
                notSelectedMessage: '',
                processingRequest: false,
                ...super.prefs()
            };
        }

        init() {
            super.init();
            this.updateSelectedState();

            // Only collapse after Delete — Confirm must stay open so the success/summary stays visible.
            const uuid = this.prefs().uuid;

            if (collapseAfterRemoveUuids.has(uuid)) {
                this.collapseContent();
                collapseAfterRemoveUuids.delete(uuid);
            }
        }

        /**
         * @description Collapses the accordion content.
         */
        collapseContent() {
            this.ref('disclosureContent').hide().attr('aria-hidden', 'true');
            this.ref('disclosureButton').attr('aria-expanded', 'false');
        }

        /**
         * @description Expands the accordion content.
         */
        expandContent() {
            this.ref('disclosureContent').show().attr('aria-hidden', 'false');
            this.ref('disclosureButton').attr('aria-expanded', 'true');
        }

        /**
         * @description Opens or closes the accordion content.
         * Kept on this widget (not a nested disclosure) so selection refs stay on orderBonusChoice.
         * @listens dom#click
         */
        toggleContent() {
            if (this.ref('disclosureContent').hasAttr('hidden')) {
                this.expandContent();
            } else {
                this.collapseContent();
            }
        }

        /**
         * @description All bonus option inputs in the selection list.
         * @returns option inputs
         */
        getOptionInputs(): Array<HTMLInputElement> {
            const selectionView = this.ref('selectionView').get();

            if (!selectionView) {
                return [];
            }

            return Array.from(selectionView.querySelectorAll<HTMLInputElement>(BONUS_OPTION_INPUT_SELECTOR));
        }

        /**
         * @description Inputs the customer has checked.
         * @returns selected option inputs
         */
        getSelectedInputs(): Array<HTMLInputElement> {
            return this.getOptionInputs().filter(input => input.checked);
        }

        /**
         * @description Confirmed gift remove controls (pid / uuid / action URL).
         * Scoped to confirmedView so selection tiles are never included.
         * @returns gifts currently in the basket for this choice
         */
        getConfirmedGifts(): Array<{ uuid: string; pid: string; url: string }> {
            const confirmedView = this.ref('confirmedView').get();

            if (!confirmedView) {
                return [];
            }

            return Array.from(confirmedView.querySelectorAll<HTMLElement>(CONFIRMED_REMOVE_SELECTOR))
                .map(button => ({
                    uuid: button.getAttribute('data-uuid') || '',
                    pid: button.getAttribute('data-pid') || '',
                    url: button.getAttribute('data-action') || ''
                }))
                .filter(gift => !!(gift.uuid && gift.url));
        }

        /**
         * @description Removes m-invalid from options and inputs.
         */
        clearInvalidState() {
            this.getOptionInputs().forEach(input => {
                input.classList.remove('m-invalid');

                const option = input.closest(OPTION_SELECTOR);

                if (option) {
                    option.classList.remove('m-invalid');
                }
            });
        }

        /**
         * @description Marks every unselected option invalid (red checkbox/radio via m-invalid).
         */
        markUnselectedInvalid() {
            this.getOptionInputs().forEach(input => {
                const option = input.closest(OPTION_SELECTOR);
                const isInvalid = input.type === 'checkbox' && !input.checked;

                input.classList.toggle('m-invalid', isInvalid);

                if (option) {
                    option.classList.toggle('m-invalid', isInvalid);
                }
            });
        }

        /**
         * @description When max checkboxes are selected, disable the remaining unchecked ones.
         */
        updateDisabledState() {
            const maxBonusItems = Number(this.prefs().maxBonusItems);
            const atMax = this.getSelectedInputs().length >= maxBonusItems;

            this.getOptionInputs().forEach(input => {
                if (input.dataset.unavailable === 'true') {
                    input.disabled = true;

                    return;
                }

                if (input.type === 'checkbox' && !input.checked) {
                    input.disabled = atMax;
                } else {
                    input.disabled = false;
                }
            });
        }

        /**
         * @description Keeps the selected count, classes, and disabled state in sync.
         */
        updateSelectedState() {
            const selectedCount = this.getSelectedInputs().length;
            const maxBonusItems = Number(this.prefs().maxBonusItems);
            const countLabel = this.ref('selectedCountLabel');

            this.ref('selectedCount').setText(String(selectedCount));

            if (countLabel.exists()) {
                countLabel
                    .toggleClass('m-complete', selectedCount > 0 && selectedCount >= maxBonusItems)
                    .toggleClass('m-incomplete', selectedCount < maxBonusItems);
            }

            this.getOptionInputs().forEach(input => {
                const option = input.closest(OPTION_SELECTOR);

                if (option) {
                    option.classList.toggle('m-selected', input.checked);
                }
            });

            this.updateDisabledState();
        }

        /**
         * @description Shows a selection rule message.
         * @param message validation text
         */
        showError(message: string) {
            const error = this.ref('selectionError');

            if (!error.exists()) {
                return;
            }

            error.setText(message);
            error.show();
        }

        /**
         * @description Hides the selection rule message.
         */
        clearError() {
            const error = this.ref('selectionError');

            if (!error.exists()) {
                return;
            }

            error.setText('');
            error.hide();
        }

        /**
         * @description True when the remove endpoint returned a usable cart model.
         * @param response remove / cart JSON
         * @returns whether the response can re-render the cart
         */
        isCartModelResponse(response: TBonusChoiceResponse): boolean {
            return !!(response && (response.success || Array.isArray(response.items) || response.cart));
        }

        /**
         * @description Removes one confirmed gift. Cart re-render keeps the accordion collapsed.
         * @param removeButton Delete control on a confirmed gift tile
         * @listens dom#click
         */
        removeConfirmedGift(removeButton: TRefElementInstance) {
            if (this.prefs().processingRequest) {
                return;
            }

            const button = removeButton.get();
            const uuid = button?.getAttribute('data-uuid') || '';
            const pid = button?.getAttribute('data-pid') || '';
            const url = button?.getAttribute('data-action') || '';

            if (!uuid || !url) {
                return;
            }

            collapseAfterRemoveUuids.add(this.prefs().uuid);
            this.setPref('processingRequest', true);
            this.clearError();

            submitFormJson(
                url,
                {
                    uuid: uuid,
                    pid: pid
                },
                'POST'
            )
                .then((response: TBonusChoiceResponse) => {
                    if (!this.isCartModelResponse(response)) {
                        throw new Error((response && response.errorMessage) || this.prefs().errorMessage);
                    }

                    const cart = response.cart || response;

                    this.eventBus().emit(EVENT_BONUS_UPDATED, cart, this);
                    this.eventBus().emit(EVENT_MINICART_NEED_UPDATE, cart, this);
                })
                .catch(() => {
                    collapseAfterRemoveUuids.delete(this.prefs().uuid);

                    const success = this.ref('selectionSuccess');

                    if (success.exists()) {
                        success.removeClass('m-success').addClass('m-error').attr('role', 'alert').setText(this.prefs().errorMessage).show();
                    } else {
                        this.showError(this.prefs().errorMessage);
                    }
                })
                .finally(() => {
                    this.setPref('processingRequest', false);
                });
        }

        /**
         * @description Removes confirmed gifts for this BDLI so the customer can pick again.
         * Basket is updated first; cart re-render shows the selection view.
         * @listens dom#click
         */
        startChangeSelection() {
            if (this.prefs().processingRequest) {
                return;
            }

            const gifts = this.getConfirmedGifts();

            if (!gifts.length) {
                return;
            }

            this.setPref('processingRequest', true);
            this.clearError();

            gifts
                .reduce<Promise<TBonusChoiceResponse | null>>(
                    (chain, gift) =>
                        chain.then(() =>
                            submitFormJson(
                                gift.url,
                                {
                                    uuid: gift.uuid,
                                    pid: gift.pid
                                },
                                'POST'
                            ).then((response: TBonusChoiceResponse) => {
                                if (!this.isCartModelResponse(response)) {
                                    throw new Error((response && response.errorMessage) || this.prefs().errorMessage);
                                }

                                return response;
                            })
                        ),
                    Promise.resolve(null)
                )
                .then(response => {
                    if (!response) {
                        return;
                    }

                    const cart = response.cart || response;

                    this.eventBus().emit(EVENT_BONUS_UPDATED, cart, this);
                    this.eventBus().emit(EVENT_MINICART_NEED_UPDATE, cart, this);
                })
                .catch(() => {
                    // selectionError sits in the (hidden) selection view while confirmed is shown —
                    // surface the failure on the visible success banner instead.
                    const success = this.ref('selectionSuccess');

                    if (success.exists()) {
                        success.removeClass('m-success').addClass('m-error').attr('role', 'alert').setText(this.prefs().errorMessage).show();
                    } else {
                        this.showError(this.prefs().errorMessage);
                    }
                })
                .finally(() => {
                    this.setPref('processingRequest', false);
                });
        }

        /**
         * @description Syncs selection UI after a radio/checkbox change.
         * @param inputRef changed option
         * @listens dom#change
         */
        handleSelectionChange(inputRef: TRefElementInstance) {
            const input = inputRef.get() as HTMLInputElement | undefined;
            const maxBonusItems = Number(this.prefs().maxBonusItems);

            if (input && input.type === 'checkbox' && input.checked && this.getSelectedInputs().length > maxBonusItems) {
                input.checked = false;
                this.showError(this.prefs().maxExceededMessage.replace('{0}', String(maxBonusItems)));
            } else {
                this.clearError();
                this.clearInvalidState();
            }

            this.updateSelectedState();
        }

        /**
         * @description Sends the current selection to Cart-AddBonusProducts.
         * Requires the full maxBonusItems count before submit.
         * @listens dom#click
         */
        confirmSelection() {
            if (this.prefs().processingRequest) {
                return;
            }

            const maxBonusItems = Number(this.prefs().maxBonusItems);
            const selected = this.getSelectedInputs();

            if (selected.length < maxBonusItems) {
                this.showError(this.prefs().notSelectedMessage);
                this.markUnselectedInvalid();

                return;
            }

            const bonusProducts = selected.map(option => ({
                pid: option.value,
                qty: 1
            }));

            this.setPref('processingRequest', true);

            submitFormJson(this.prefs().actionUrl, {
                pids: JSON.stringify({
                    bonusProducts: bonusProducts,
                    totalQty: maxBonusItems
                }),
                duuid: this.prefs().uuid,
                pliuuid: ''
            })
                .then((response: TBonusChoiceResponse) => {
                    if (response && response.success && response.cart) {
                        this.clearError();
                        this.clearInvalidState();
                        this.eventBus().emit(EVENT_BONUS_UPDATED, response.cart, this);
                        this.eventBus().emit(EVENT_MINICART_NEED_UPDATE, response.cart, this);

                        return;
                    }

                    this.showError((response && response.errorMessage) || this.prefs().errorMessage);
                })
                .catch(() => {
                    this.showError(this.prefs().errorMessage);
                })
                .finally(() => {
                    this.setPref('processingRequest', false);
                });
        }
    }

    return OrderBonusChoice;
}

export type TOrderBonusChoice = ReturnType<typeof OrderBonusChoiceClassCreator>;

export type TOrderBonusChoiceInstance = InstanceType<TOrderBonusChoice>;

export default OrderBonusChoiceClassCreator;
